import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '../common/Navbar';
import {
  ArrowLeft,
  Edit,
  Calendar,
  User,
  Building,
  Mail,
  Phone,
  Globe,
  MapPin,
  DollarSign,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  MessageSquare,
  Save,
  X
} from 'lucide-react';
import { leadService } from '../../services/api';

export default function LeadDetailView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStatus, setEditingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [lookups, setLookups] = useState({ leadStatuses: [] });

  useEffect(() => {
    loadLeadDetails();
    loadLookups();
  }, [id]);

  const loadLookups = async () => {
    try {
      const response = await leadService.getLookups();
      setLookups(response.data.data);
    } catch (error) {
      console.error('Error loading lookups:', error);
    }
  };

  const loadLeadDetails = async () => {
    try {
      setLoading(true);
      const response = await leadService.getById(id);
      const leadData = response.data.data.lead || response.data.data;
      setLead(leadData);
      setNewStatus(leadData.status_id || '');
      
      try {
        const followUpResponse = await leadService.getFollowUpHistory(id);
        setFollowUps(followUpResponse.data.data || []);
      } catch (followUpError) {
        console.error('Error loading follow-ups:', followUpError);
        setFollowUps([]);
      }
    } catch (error) {
      console.error('Error loading lead details:', error);
      alert('Failed to load lead details');
      navigate('/admin/leads');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!newStatus) {
      alert('Please select a status');
      return;
    }

    try {
      await leadService.updateStatus(id, {
        status_id: newStatus,
        note: statusNote
      });
      
      alert('✅ Status updated successfully!');
      setEditingStatus(false);
      setStatusNote('');
      loadLeadDetails();

      const selectedStatus = lookups.leadStatuses.find(s => s.id == newStatus);
      if (selectedStatus && selectedStatus.name === 'Project execution') {
        alert('🎉 Lead moved to Project execution! It will now appear in the Projects page.');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('❌ Failed to update status');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysUntilFollowUp = (date) => {
    if (!date) return null;
    const today = new Date();
    const followUpDate = new Date(date);
    const diffTime = followUpDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getFollowUpStatus = (date) => {
    const days = getDaysUntilFollowUp(date);
    if (days === null) return { text: 'No follow-up scheduled', color: 'text-slate-400', bg: 'bg-slate-800' };
    if (days < 0) return { text: `Overdue by ${Math.abs(days)} days`, color: 'text-red-400', bg: 'bg-red-500/20' };
    if (days === 0) return { text: 'Due today', color: 'text-orange-400', bg: 'bg-orange-500/20' };
    if (days <= 7) return { text: `Due in ${days} days`, color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    return { text: `Due in ${days} days`, color: 'text-green-400', bg: 'bg-green-500/20' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-400 text-lg">Loading lead details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">Lead not found</p>
          </div>
        </div>
      </div>
    );
  }

  const followUpStatus = getFollowUpStatus(lead.next_followup_date);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Navbar />
      
      <div className="p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/leads')}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-white mb-1">{lead.organisation_name}</h1>
              <p className="text-slate-400">Lead Details & History</p>
            </div>
          </div>
          <Link
            to={`/admin/leads/edit/${id}`}
            className="px-6 py-3 bg-amber-600 hover:bg-amber-500 text-white rounded-lg font-semibold flex items-center gap-2 transition-colors shadow-lg"
          >
            <Edit className="w-5 h-5" />
            Edit Lead
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status Card - FIXED: Removed status_color */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-amber-400" />
                  Current Status
                </h2>
                {!editingStatus && (
                  <button
                    onClick={() => setEditingStatus(true)}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm font-semibold transition-colors"
                  >
                    Change Status
                  </button>
                )}
              </div>

              {editingStatus ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">New Status</label>
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      {lookups.leadStatuses.map(status => (
                        <option key={status.id} value={status.id}>{status.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Status Change Note</label>
                    <textarea
                      value={statusNote}
                      onChange={(e) => setStatusNote(e.target.value)}
                      placeholder="Add a note about this status change..."
                      rows={3}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleStatusChange}
                      className="flex-1 px-4 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                    >
                      <Save className="w-4 h-4" />
                      Save Status
                    </button>
                    <button
                      onClick={() => {
                        setEditingStatus(false);
                        setNewStatus(lead.status_id);
                        setStatusNote('');
                      }}
                      className="px-4 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-semibold flex items-center gap-2 transition-colors"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <span className="px-6 py-3 bg-amber-500/20 text-amber-400 rounded-lg text-lg font-bold">
                    {lead.status_name || lead.status}
                  </span>
                  {lead.next_followup_date && (
                    <div className={`px-4 py-2 rounded-lg ${followUpStatus.bg}`}>
                      <p className={`text-sm font-semibold ${followUpStatus.color}`}>
                        {followUpStatus.text}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Contact Information */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-amber-400" />
                Contact Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-slate-500 mt-1" />
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Client Name</p>
                    <p className="text-white font-medium">{lead.client_name || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-slate-500 mt-1" />
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Email</p>
                    <a href={`mailto:${lead.client_email}`} className="text-blue-400 hover:underline">
                      {lead.client_email || 'N/A'}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-slate-500 mt-1" />
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Phone</p>
                    <p className="text-white">{lead.client_phone || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building className="w-5 h-5 text-slate-500 mt-1" />
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Position</p>
                    <p className="text-white">{lead.client_position || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Globe className="w-5 h-5 text-slate-500 mt-1" />
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Website</p>
                    {lead.website ? (
                      <a href={lead.website} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                        {lead.website}
                      </a>
                    ) : (
                      <p className="text-white">N/A</p>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-slate-500 mt-1" />
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Location</p>
                    <p className="text-white">{lead.country_name || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-400" />
                Project Details
              </h2>
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Project Name</p>
                  <p className="text-white font-medium">{lead.project_name || 'N/A'}</p>
                </div>
                {lead.project_description && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Description</p>
                    <p className="text-slate-300 leading-relaxed">{lead.project_description}</p>
                  </div>
                )}
                {lead.final_project_description && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Final Project Description</p>
                    <p className="text-slate-300 leading-relaxed">{lead.final_project_description}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-800">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Sector</p>
                    <p className="text-white">{lead.sector_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Source</p>
                    <p className="text-white">{lead.source_name || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Follow-Up History */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-amber-400" />
                Follow-Up History
              </h2>
              
              {followUps.length > 0 ? (
                <div className="space-y-4">
                  {followUps.map((followUp, index) => (
                    <div
                      key={index}
                      className="bg-slate-800 rounded-lg p-4 border-l-4"
                      style={{ borderColor: followUp.is_completed ? '#10b981' : '#f59e0b' }}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {followUp.is_completed ? (
                            <CheckCircle className="w-5 h-5 text-green-400" />
                          ) : (
                            <Clock className="w-5 h-5 text-amber-400" />
                          )}
                          <span className="text-white font-semibold">
                            {formatDate(followUp.follow_up_date)}
                          </span>
                        </div>
                        <span className="text-xs text-slate-400">
                          {formatDateTime(followUp.created_at)}
                        </span>
                      </div>
                      {followUp.notes && (
                        <p className="text-slate-300 leading-relaxed mt-2 pl-7">
                          {followUp.notes}
                        </p>
                      )}
                      {followUp.user_name && (
                        <p className="text-xs text-slate-500 mt-2 pl-7">
                          By: {followUp.user_name}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                  <p className="text-slate-400">No follow-up history recorded</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Summary Cards */}
          <div className="space-y-6">
            {/* Financial Summary */}
            <div className="bg-gradient-to-br from-amber-600 to-orange-600 rounded-xl p-6 text-white shadow-xl">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-6 h-6" />
                <h3 className="font-semibold">Estimated Revenue</h3>
              </div>
              <p className="text-3xl font-bold mb-1">{formatCurrency(lead.estimated_revenue || 0)}</p>
              <p className="text-sm opacity-90">Potential value</p>
            </div>

            {/* Key Dates */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-amber-400" />
                Key Dates
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Date Contacted</p>
                  <p className="text-white font-medium">{formatDate(lead.date_contacted)}</p>
                </div>
                {lead.next_followup_date && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Next Follow-Up</p>
                    <p className={`font-medium ${followUpStatus.color}`}>
                      {formatDate(lead.next_followup_date)}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-500 mb-1">Last Updated</p>
                  <p className="text-slate-300 text-sm">{formatDateTime(lead.updated_at)}</p>
                </div>
              </div>
            </div>

            {/* Assignment */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-amber-400" />
                Assignment
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Consultant</p>
                  <p className="text-white font-medium">{lead.consultant_name || 'Unassigned'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Created By</p>
                  <p className="text-slate-300">{lead.created_by_name || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
              <h3 className="text-lg font-bold text-white mb-4">Quick Actions</h3>
              <div className="space-y-2">
                {lead.client_email && (
                  <a
                    href={`mailto:${lead.client_email}`}
                    className="block w-full px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-center transition-colors"
                  >
                    Send Email
                  </a>
                )}
                {lead.client_phone && (
                  <a
                    href={`tel:${lead.client_phone}`}
                    className="block w-full px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-center transition-colors"
                  >
                    Call Client
                  </a>
                )}
                <Link
                  to={`/admin/leads/edit/${id}`}
                  className="block w-full px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-center transition-colors"
                >
                  Edit Lead
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}